/**
 * «Yo» — todo lo que es de la persona y sus temas.
 *   · PantallaYo: hoja a pantalla completa del botón «Yo» de la barra inferior del celular.
 *   · PaginaYo: la misma información en escritorio, en la ruta /yo (perfil a la izquierda, secciones en dos columnas).
 * Contenido: tarjeta de perfil (foto, cargo, avance de hoy y cumplimiento del mes) · Mi trabajo · Mi desempeño · Mi cuenta ·
 * «Ver todo el OS» (solo celular) y cerrar sesión.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../lib/icons.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { api } from "../../api/client.js";
import { cn } from "../../lib/utils.js";
import UserAvatar from "../UserAvatar.jsx";
import { EVENTO_ABRIR_BIENVENIDA } from "../BienvenidaSOA10.jsx";
import { useFlag } from "../../lib/useFlags.js";

const TONOS = { c: ["#CFFAFE", "#155E75"], g: ["#D1FAE5", "#065F46"], v: ["#EDE9FE", "#5B21B6"], a: ["#FEF3C7", "#92400E"], n: ["#E4E6EB", "#2B2E36"] };

function Fila({ icono, tono = "c", titulo, detalle, onClick }) {
  const [fondo, color] = TONOS[tono] || TONOS.c;
  return (
    <button type="button" onClick={onClick} className="w-full flex items-center gap-3 px-4 min-h-[64px] text-left active:bg-muted/60 hover:bg-muted/40 transition-colors">
      <span className="h-11 w-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: fondo, color }}>
        <Icon name={icono} className="text-[24px]" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[16px] font-extrabold leading-tight">{titulo}</span>
        {detalle && <span className="block text-[13px] text-muted-foreground truncate">{detalle}</span>}
      </span>
      <Icon name="chevron_right" className="text-[22px] text-muted-foreground" />
    </button>
  );
}

function Grupo({ titulo, children, className }) {
  const hijos = (Array.isArray(children) ? children : [children]).filter(Boolean);
  if (hijos.length === 0) return null;
  return (
    <section className={className}>
      <h3 className="px-5 pb-2 text-[13px] font-extrabold uppercase tracking-wider text-muted-foreground">{titulo}</h3>
      <div className="mx-4 md:mx-0 rounded-3xl bg-card shadow-[0_1px_3px_rgba(17,19,24,0.06)] divide-y divide-border/60 overflow-hidden">{hijos}</div>
    </section>
  );
}

// Datos vivos de la tarjeta de perfil: avance de hoy y cumplimiento de los últimos 30 días.
function useDatosYo(activo, miembroId) {
  const [dia, setDia] = useState(null);
  const [score, setScore] = useState(null);
  useEffect(() => {
    if (!activo || !miembroId) return;
    api.miDia().then(setDia).catch(() => {});
    api.request("GET", "/api/cumplimiento/mi-score?dias=30").then(setScore).catch(() => {});
  }, [activo, miembroId]);
  const total = Number(dia?.total) || 0, hechas = Number(dia?.completadas) || 0;
  return {
    total, hechas,
    pct: total > 0 ? Math.round((hechas / total) * 100) : null,
    cumpl: typeof score?.score === "number" ? Math.round(score.score) : null,
  };
}

function TarjetaPerfil({ miembro, datos, ir }) {
  const cargo = [miembro.titulo, miembro.rol].filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i).join(" · ");
  return (
    <div className="rounded-3xl bg-card p-5 shadow-[0_1px_3px_rgba(17,19,24,0.06)]">
      <button type="button" onClick={() => ir("/mi-perfil")} className="w-full flex items-center gap-4 text-left">
        <UserAvatar user={miembro} className="h-[72px] w-[72px]" fallbackClassName="text-[24px]" />
        <span className="flex-1 min-w-0">
          <span className="block text-[22px] font-extrabold leading-tight truncate">{miembro.nombre}</span>
          <span className="block text-[14px] text-muted-foreground truncate">{cargo || "Equipo"}</span>
          <span className="block text-[13px] text-muted-foreground truncate">{miembro.email}</span>
        </span>
        <Icon name="chevron_right" className="text-[22px] text-muted-foreground" />
      </button>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <button type="button" onClick={() => ir("/inicio")} className="rounded-2xl bg-[#CFFAFE] text-[#155E75] p-3 text-left">
          <span className="block text-[26px] font-extrabold leading-none tabular-nums">{datos.total > 0 ? `${datos.hechas}/${datos.total}` : "—"}</span>
          <span className="block text-[13px] font-extrabold mt-1">Hoy{datos.pct != null ? ` · ${datos.pct}%` : ""}</span>
        </button>
        <button type="button" onClick={() => ir(`/equipo/${miembro.id}/scorecard`)} className="rounded-2xl bg-[#D1FAE5] text-[#065F46] p-3 text-left">
          <span className="block text-[26px] font-extrabold leading-none tabular-nums">{datos.cumpl != null ? `${datos.cumpl}%` : "—"}</span>
          <span className="block text-[13px] font-extrabold mt-1">Cumplimiento · 30 días</span>
        </button>
      </div>
    </div>
  );
}

// Las tres secciones, iguales en celular y escritorio.
// «Mis roles»: qué soy en la agencia, en qué cuentas y de qué departamento soy jefe (motor de roles, /api/me/roles).
function useMisRoles(activo) {
  const [lentes, setLentes] = useState(null);
  useEffect(() => {
    if (!activo) return;
    api.request("GET", "/api/me/roles").then(r => setLentes(Array.isArray(r?.lentes) ? r.lentes : [])).catch(() => setLentes([]));
  }, [activo]);
  return lentes;
}

function Secciones({ a, miembro, ir, onBienvenida, className }) {
  const tieneClientes = a.isCuenta || a.isCM || a.isAdmin || a.isCOO || a.isCCC;
  const lentes = useMisRoles(!!miembro?.id);
  const tutorialesOn = useFlag("ff_academia");
  const triageOn = useFlag("ff_triage");
  return (
    <>
      <Grupo titulo="Mi trabajo" className={className}>
        <Fila icono="inbox" tono="c" titulo="Novedades" detalle="Lo que te escribieron y espera tu respuesta" onClick={() => ir("/novedades")} />
        <Fila icono="mail" tono="c" titulo={triageOn ? "Triage" : "Bandeja"} detalle={triageOn ? "Lo que hay que decidir hoy" : "Las bandejas de tu rol"} onClick={() => ir(triageOn ? "/triage" : "/inbox-hub")} />
        <Fila icono="today" tono="c" titulo="Mi Día" detalle="Lo que tengo para hoy" onClick={() => ir("/inicio")} />
        <Fila icono="task_alt" tono="c" titulo="Mis tareas" detalle="Solo las que me asignaron" onClick={() => ir("/tareas?mis=1")} />
        <Fila icono="calendar_month" tono="c" titulo="Mi agenda" detalle="Coberturas, reuniones y entregas" onClick={() => ir("/agenda")} />
        <Fila icono="location_on" tono="g" titulo="Hoy en campo" detalle="Cómo llegar, lista de salida y Llegué / Terminé" onClick={() => ir("/campo")} />
        <Fila icono="schedule" tono="c" titulo="Mis horas" detalle="Tiempo cargado y estimado" onClick={() => ir("/horas")} />
        {tieneClientes && <Fila icono="groups" tono="c" titulo="Mis clientes" detalle="Mi cartera" onClick={() => ir("/mis-clientes")} />}
        {(a.isCM || a.isEspecialista) && <Fila icono="view_kanban" tono="c" titulo="Mi pipeline" detalle="Mis piezas por etapa" onClick={() => ir("/mi-pipeline")} />}
      </Grupo>
      {lentes && lentes.length > 1 && (
        <Grupo titulo="Mis roles" className={className}>
          {lentes.map((l, i) => (
            <Fila key={`${l.rol}-${l.departamento_id || i}`} icono={l.origen === "departamento" ? "apartment" : l.origen === "cuenta" ? "badge" : "workspace_premium"} tono={l.origen === "departamento" ? "v" : l.origen === "cuenta" ? "c" : "n"}
              titulo={l.etiqueta} detalle={l.origen === "global" ? "Mi rol en la agencia" : l.origen === "cuenta" ? "En las cuentas donde lo ocupo" : "Abrir el panel del departamento"}
              onClick={l.origen === "departamento" ? () => ir(`/departamento/${l.departamento_id}`) : undefined} />
          ))}
        </Grupo>
      )}
      <Grupo titulo="Mi desempeño" className={className}>
        <Fila icono="insights" tono="g" titulo="Mi scorecard" detalle="Cumplimiento y entregas" onClick={() => ir(`/equipo/${miembro.id}/scorecard`)} />
        <Fila icono="emoji_events" tono="g" titulo="Podio y logros" detalle="Ranking y reconocimientos del equipo" onClick={() => ir("/gamificacion/podio")} />
        <Fila icono="gavel" tono="g" titulo="Apelaciones" detalle="Pedir revisar una penalización" onClick={() => ir("/apelaciones")} />
        <Fila icono="forum" tono="g" titulo="Feedback" detalle="Lo que dicen los clientes" onClick={() => ir("/feedback")} />
      </Grupo>
      <Grupo titulo="Mi cuenta" className={className}>
        <Fila icono="person" tono="v" titulo="Mi perfil" detalle="Foto, WhatsApp y cumpleaños" onClick={() => ir("/mi-perfil")} />
        <Fila icono="notifications" tono="v" titulo="Avisos y notificaciones" detalle="Sonido, tipos y horario de silencio" onClick={() => ir("/settings/notificaciones")} />
        <Fila icono="slideshow" tono="a" titulo="Presentación del sistema" detalle="Qué es SOA 10 PRO y cómo sacarle el jugo" onClick={() => ir("/presentacion")} />
        <Fila icono="help" tono="a" titulo="Ver bienvenida" detalle="Repasar lo nuevo de SOA 10 PRO" onClick={onBienvenida} />
        <Fila icono="menu_book" tono="a" titulo="Ayuda y procedimientos" detalle="Cómo se hace cada cosa" onClick={() => ir("/procedimientos")} />
        {tutorialesOn && <Fila icono="school" tono="a" titulo="Tutoriales" detalle="Aprendé el SOA 10 PRO paso a paso" onClick={() => ir("/ayuda/tutoriales")} />}
      </Grupo>
    </>
  );
}

/** Celular: hoja a pantalla completa. */
export default function PantallaYo({ abierta, onCerrar, onVerTodo }) {
  const navigate = useNavigate();
  const a = useAuth();
  const { miembro, signOut } = a;
  const datos = useDatosYo(abierta, miembro?.id);

  useEffect(() => {
    if (!abierta) return;
    const onKey = (e) => { if (e.key === "Escape") onCerrar(); };
    document.addEventListener("keydown", onKey);
    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = previo; };
  }, [abierta, onCerrar]);

  if (!abierta || !miembro) return null;
  const ir = (ruta) => { onCerrar(); navigate(ruta); };

  return (
    <div role="dialog" aria-modal="true" aria-label="Yo" className="md:hidden fixed inset-0 z-[9990] bg-background flex flex-col">
      <div className="flex items-center gap-2 px-4 pt-[max(12px,env(safe-area-inset-top))] pb-2 flex-shrink-0">
        <h2 className="flex-1 text-[24px] font-extrabold tracking-tight">Yo</h2>
        <button onClick={onCerrar} aria-label="Cerrar" className="h-11 w-11 rounded-full bg-card flex items-center justify-center"><Icon name="close" className="text-[22px]" /></button>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain pb-[max(24px,env(safe-area-inset-bottom))] space-y-5">
        <div className="mx-4"><TarjetaPerfil miembro={miembro} datos={datos} ir={ir} /></div>
        <Secciones a={a} miembro={miembro} ir={ir} onBienvenida={() => { onCerrar(); window.dispatchEvent(new Event(EVENTO_ABRIR_BIENVENIDA)); }} className="" />
        <div className="mx-4 space-y-3">
          <button type="button" onClick={() => { onCerrar(); onVerTodo?.(); }} className="w-full h-14 rounded-2xl bg-card text-[16px] font-extrabold flex items-center justify-center gap-2 shadow-[0_1px_3px_rgba(17,19,24,0.06)]">
            <Icon name="apps" className="text-[22px]" />Ver todo el OS
          </button>
          <button type="button" onClick={() => { onCerrar(); signOut(); }} className="w-full h-14 rounded-2xl text-[16px] font-extrabold text-[#B91C1C] flex items-center justify-center gap-2">
            <Icon name="logout" className="text-[22px]" />Cerrar sesión
          </button>
          <p className="text-center text-[12px] text-muted-foreground">THE CREW AGENCIA · SOA 10 PRO</p>
        </div>
      </div>
    </div>
  );
}

/** Escritorio: /yo — perfil a la izquierda, las tres secciones al lado. */
export function PaginaYo() {
  const navigate = useNavigate();
  const a = useAuth();
  const { miembro, signOut } = a;
  const datos = useDatosYo(true, miembro?.id);
  if (!miembro) return null;
  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
      <h1 className="text-[28px] font-extrabold tracking-tight mb-5">Yo</h1>
      <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)] items-start">
        <div className="space-y-4 lg:sticky lg:top-4">
          <TarjetaPerfil miembro={miembro} datos={datos} ir={navigate} />
          <button type="button" onClick={() => signOut()} className="w-full h-12 rounded-2xl bg-card text-[15px] font-extrabold text-[#B91C1C] flex items-center justify-center gap-2 shadow-[0_1px_3px_rgba(17,19,24,0.06)] hover:bg-muted/40">
            <Icon name="logout" className="text-[20px]" />Cerrar sesión
          </button>
        </div>
        <div className={cn("grid gap-6 xl:grid-cols-2 items-start")}>
          <Secciones a={a} miembro={miembro} ir={navigate} onBienvenida={() => window.dispatchEvent(new Event(EVENTO_ABRIR_BIENVENIDA))} className="" />
        </div>
      </div>
    </div>
  );
}
